import React, { Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { profileSlice } from "redux/features/profile";
import {
  Element,
  Message,
  Form,
  Columns,
  Container,
} from "react-bulma-components";
import { dualSort, caseInsensitive, ascending } from "util/sort";
import IconButton from "util/iconButton";
import ErrorMessage from "util/errorMessage";
import { FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import MathContent from "util/mathjax";
import { useProfileResource } from "redux/features/profileResources";

const { Field, Control, Label, Checkbox, Select, Radio, Help } = Form;

function ActionButtons({
  saveWording,
  saveIcon,
  onSubmit,
  onCancel,
  pending,
  errors,
}) {
  return (
    <Fragment>
      {errors.map((message, position) => (
        <ErrorMessage>Unable to save changes: {message}</ErrorMessage>
      ))}
      <div className="buttons is-centered">
        <IconButton disabled={pending} icon={saveIcon} onClick={onSubmit}>
          {saveWording}
        </IconButton>
        <IconButton disabled={pending} icon={FaTimes} onClick={onCancel}>
          Cancel Changes
        </IconButton>
      </div>
    </Fragment>
  );
}

function PromptForActivityRegistration({
  title,
  id,
  dates,
  baseActivityId,
  forProgram,
  directlyConfirmable,
  incompleteRegistration,
  incompletePayment,
}) {
  const { id: personId } = useSelector(profileSlice.selector);
  const isSameActivity = id === baseActivityId;

  const baseParticipation = useProfileResource(
    personId,
    "participations",
    personId + "/" + baseActivityId
  );

  const targetParticipation = useProfileResource(
    personId,
    "participations",
    personId + "/" + id
  );

  const justRegisteredTarget =
    targetParticipation.data && targetParticipation.data.id && !isSameActivity;
  const reloadBase = baseParticipation.reload;
  useEffect(() => {
    if (justRegisteredTarget) reloadBase();
  }, [justRegisteredTarget, reloadBase]);

  const baseChanges = baseParticipation.changeRecord
    ? baseParticipation.changeRecord.changes
    : {};

  const targetChanges = targetParticipation.changeRecord
    ? targetParticipation.changeRecord.changes
    : {};

  const confirming = targetChanges.attending;
  const initiatingRegistration = confirming && !directlyConfirmable;
  const forSeries = !isSameActivity && !forProgram;
  const declining = targetChanges.attending === false;
  const decliningAll = baseChanges.not_attending_in_future && forSeries;

  const setConfirming = () => {
    targetParticipation.updateFrom("attending", true, null);
    if (forSeries)
      baseParticipation.clearChangedValue("not_attending_in_future");
  };

  const setDeclining = () => {
    targetParticipation.updateFrom("attending", false, null);
    if (forSeries)
      baseParticipation.clearChangedValue("not_attending_in_future");
  };
  const navigatingToRegistration =
    initiatingRegistration || incompleteRegistration;
  const navigate = useNavigate();
  const submitChanges = () => {
    if (navigatingToRegistration) {
      navigate(
        "participation/" + (forProgram ? baseActivityId : id) + "?register"
      );
    } else {
      targetParticipation.save();
      if (!isSameActivity) baseParticipation.save();
    }
  };
  const cancelChanges = () => {
    targetParticipation.clear();
    if (!isSameActivity) baseParticipation.clear();
  };

  const setDecliningAll = () => {
    targetParticipation.clearChangedValue("attending");
    baseParticipation.updateFrom("not_attending_in_future", true, false);
  };

  const haveChanges =
    baseParticipation.changeRecord || targetParticipation.changeRecord;

  const savePending =
    (baseParticipation.changeRecord &&
      baseParticipation.changeRecord.pending) ||
    (targetParticipation.changeRecord &&
      targetParticipation.changeRecord.pending);

  const changeRecords = isSameActivity
    ? [targetParticipation.changeRecord]
    : [targetParticipation.changeRecord, baseParticipation.changeRecord];

  const saveErrors = changeRecords
    .filter((r) => r && r.failed)
    .map((r) => r.failed.message);

  if (justRegisteredTarget) return null;

  return (
    <Fragment>
      <Field className="prompt-activity-registration">
        <Label size="medium" className="activity-title">
          <MathContent>{title}</MathContent>
          <Help className="dates">{dates}</Help>
        </Label>
        {incompleteRegistration ? (
          <b>Registration is incomplete: you have unsaved changes.</b>
        ) : incompletePayment ? (
          <p>
            Full payment not yet made;{" "}
            <Link to={"participation/" + id}>make full payment now</Link>
          </p>
        ) : (
          <Fragment>
            <Control>
              <Radio
                name={"rsvp-attending-" + id}
                value="yes"
                checked={!!confirming}
                onChange={setConfirming}
              >
                {directlyConfirmable
                  ? "Yes, I am attending"
                  : "Yes, I plan to attend; take me to the registration form"}
              </Radio>
            </Control>
            <Control>
              {" "}
              <Radio
                name={"rsvp-attending-" + id}
                value="no"
                checked={!!declining}
                onChange={setDeclining}
              >
                No, I will not attend
                {forSeries
                  ? ", but may attend future events in the series"
                  : null}
              </Radio>
            </Control>
            {forSeries ? (
              <Control>
                <Radio
                  name={"rsvp-attending-" + id}
                  value="no-to-all"
                  checked={!!decliningAll}
                  onChange={setDecliningAll}
                >
                  I don't plan on attending this or future events in the series
                </Radio>
              </Control>
            ) : null}
          </Fragment>
        )}
      </Field>
      {incompleteRegistration || haveChanges ? (
        <ActionButtons
          saveWording={
            navigatingToRegistration
              ? (incompleteRegistration ? "Resume" : "Launch") +
                " Registration Form"
              : "Submit Response"
          }
          saveIcon={navigatingToRegistration ? FaEdit : FaCheck}
          onSubmit={submitChanges}
          onCancel={cancelChanges}
          pending={savePending}
          errors={saveErrors}
        />
      ) : null}
    </Fragment>
  );
}

function TalkPromptWrapper({ children }) {
  return <Element mx={2}>{children}</Element>;
}

const IN_PERSON = "in person";
const REMOTELY = "remotely";

function TalkPrompt({
  id,
  label,
  has_remote_option,
  changes,
  updateFrom,
  clearChangedValue,
}) {
  const field = "attending_talk_" + id;
  const value = changes[field];
  const setAttending = (attending) => {
    updateFrom(field, attending, null);
    if (attending) clearChangedValue("not_attending_in_future");
  };
  return (
    <Columns multiline={false} className="my-0">
      <Columns.Column narrow className="py-0">
        <Control>
          {has_remote_option ? (
            <Fragment>
              <Radio
                name={field}
                value={IN_PERSON}
                checked={value === IN_PERSON}
                onChange={(e) => setAttending(IN_PERSON)}
              >
                Yes - in person
              </Radio>
              <Radio
                name={field}
                value={REMOTELY}
                checked={value === REMOTELY}
                onChange={(e) => setAttending(REMOTELY)}
              >
                Yes - remotely
              </Radio>
            </Fragment>
          ) : (
            <Radio
              name={field}
              value={1}
              checked={value === true}
              onChange={(e) => setAttending(true)}
            >
              Yes
            </Radio>
          )}
          <Radio
            name={field}
            value=""
            checked={value === false}
            onChange={(e) => setAttending(false)}
          >
            No
          </Radio>
        </Control>
      </Columns.Column>
      <Columns.Column className="py-0">
        <MathContent>{label}</MathContent>
      </Columns.Column>
    </Columns>
  );
}

function OtherTalksPrompt({ children }) {
  return <Element>{children}</Element>;
}

function NotInFuturePrompt({
  upcomingTalks,
  ongoingLabel,
  changes,
  updateFrom,
}) {
  const field = "not_attending_in_future";
  // Get the ids of all upcoming talks, so we can explicitly set attendance
  // on them to false when "not attending in future" is set. Note that
  // we have to take care to distinguish between the regular upcoming
  // talk records that have a single "id" defined, and the last one that
  // might have an "additional_talks" list of ids defined.
  const upcomingTalkIds = upcomingTalks
    .map(({ id, additional_talks }) => additional_talks ?? [id])
    .flat();
  const onChange = (selected) => {
    updateFrom(field, selected, false);
    if (selected)
      upcomingTalkIds.forEach((id) =>
        updateFrom("attending_talk_" + id, false, null)
      );
  };
  const thisOrThese = upcomingTalks.length === 1 ? "this" : "these";
  return (
    <Element>
      <Control>
        <Checkbox
          checked={changes[field]}
          onChange={(e) => onChange(!!e.target.value)}
        >
          I will not be attending {thisOrThese} or any future talks in this{" "}
          {ongoingLabel}.
        </Checkbox>
      </Control>
    </Element>
  );
}

function PromptForTalkAttendances({
  activityId,
  activityTitle,
  upcomingTalks,
  pastTalks,
  ongoingLabel,
}) {
  const { id: personId } = useSelector(profileSlice.selector);

  const {
    changeRecord,
    updateFrom,
    clearChangedValue,
    save,
    clear,
  } = useProfileResource(
    personId,
    "participations",
    personId + "/" + activityId
  );
  const changes = changeRecord ? changeRecord.changes : {};
  const upcomingTalkOrTalks =
    "Upcoming " + (upcomingTalks.length === 1 ? "talk" : "talks");
  const pastTalkOrTalks =
    "Past " +
    (pastTalks.length === 1 && pastTalks[0].id !== undefined
      ? "talk"
      : "talks");
  // We can have a not_attending_in_future entry from a "related_activities"
  // future activity prompt as well as am upcoming talks prompt; don't
  // display save/cancel buttons here as well in that case.
  const haveSaveableItems =
    [...upcomingTalks, ...pastTalks].filter((t) => t.id !== undefined).length >
    0;

  return (
    <Fragment>
      <Field className="prompt-activity-registration">
        <Label size="medium" className="activity-title">
          {activityTitle}
        </Label>
        {upcomingTalks.length > 0 ? (
          <Fragment>
            <Label className="activity-talk-group" size="small">
              {upcomingTalkOrTalks}: will you attend?
            </Label>
            <TalkPromptWrapper>
              {upcomingTalks.map(
                ({ id, label, has_remote_option, additional_talks }) =>
                  additional_talks ? (
                    <OtherTalksPrompt key="other">
                      Plus {additional_talks.length} later talks; please use the{" "}
                      <Link to={"participation/" + activityId}>
                        full registration page
                      </Link>{" "}
                      if you're ready to register for those as well.
                    </OtherTalksPrompt>
                  ) : (
                    <TalkPrompt
                      key={id}
                      {...{
                        id,
                        label,
                        has_remote_option,
                        changes,
                        updateFrom,
                        clearChangedValue,
                      }}
                    />
                  )
              )}
              {ongoingLabel ? (
                <NotInFuturePrompt
                  {...{ upcomingTalks, ongoingLabel, changes, updateFrom }}
                />
              ) : null}
            </TalkPromptWrapper>
          </Fragment>
        ) : null}
        {pastTalks.length > 0 ? (
          <Fragment>
            <Label className="activity-talk-group">
              {pastTalkOrTalks}: did you attend?
            </Label>
            <TalkPromptWrapper>
              {pastTalks.map(({ id, label, has_remote_option, num_talks }) =>
                num_talks ? (
                  <OtherTalksPrompt key="other">
                    For {num_talks} past talks we don't have attendance
                    information for you; please go to the{" "}
                    <Link to={"participation/" + activityId}>
                      registration page
                    </Link>{" "}
                    and let us know which one(s) you attended.
                  </OtherTalksPrompt>
                ) : (
                  <TalkPrompt
                    key={id}
                    {...{
                      id,
                      label,
                      has_remote_option,
                      changes,
                      updateFrom,
                      clearChangedValue,
                    }}
                  />
                )
              )}
            </TalkPromptWrapper>
          </Fragment>
        ) : null}
      </Field>
      {changeRecord && haveSaveableItems ? (
        <div className="buttons is-centered">
          <IconButton icon={FaCheck} onClick={save}>
            Submit Response
          </IconButton>
          <IconButton icon={FaTimes} onClick={clear}>
            Cancel Changes
          </IconButton>
        </div>
      ) : null}
    </Fragment>
  );
}

function ResponsesRelatedToParticipation({ participationSummary }) {
  const { id: personId } = useSelector(profileSlice.selector);

  const { changeRecord } = useProfileResource(
    personId,
    "participations",
    personId + "/" + participationSummary.activity.id
  );
  const ask = participationSummary.items_needing_response;

  // Look for changes made by something other than the simple RSVP dashboard
  // panel, and don't show the simple panel if such changes are pending.

  // Start with all changes, then eliminate ones made by the simple panel.
  const changesFromElsewhere = changeRecord ? { ...changeRecord.changes } : {};

  // Attendance at the current activity could have been set by the panel
  if (ask.this_activity) delete changesFromElsewhere.attending;

  // Attendance at upcoming talks are handled by the panel.
  ask.upcoming_talks
    .filter(({ id }) => id !== undefined)
    .forEach(({ id }) => delete changesFromElsewhere["attending_talk_" + id]);

  // Attendance at "additional" upcoming talks could be set to false by the
  // panel if the person has selected the "not attending in future" option.
  if (changesFromElsewhere.not_attending_in_future) {
    ask.upcoming_talks
      .filter(({ additional_talks }) => !!additional_talks)
      .forEach(({ additional_talks }) =>
        additional_talks.forEach((id) => {
          if (changesFromElsewhere["attending_talk_" + id] === false)
            delete changesFromElsewhere["attending_talk_" + id];
        })
      );
  }

  // The not_attending_in_future option could be set by us if we have
  // related activities or talks in an ongoing series.
  if (ask.related_activities.length > 0 || ask.ongoing_label)
    delete changesFromElsewhere.not_attending_in_future;

  // Past talk attendance could be set by us.
  ask.past_talks.forEach(
    ({ id }) => delete changesFromElsewhere["attending_talk_" + id]
  );

  // Anything else was set elsewhere.
  const haveUnsavedBaseChanges = Object.keys(changesFromElsewhere).length > 0;

  return (
    <Fragment>
      {ask.this_activity || haveUnsavedBaseChanges ? (
        <PromptForActivityRegistration
          title={participationSummary.activity.title}
          id={participationSummary.activity.id}
          dates={participationSummary.activity.date_string}
          baseActivityId={participationSummary.activity.id}
          incompleteRegistration={haveUnsavedBaseChanges}
          incompletePayment={
            ask.this_activity && ask.this_activity.incomplete_payment
          }
        />
      ) : null}
      {haveUnsavedBaseChanges ? null : (
        <Fragment>
          {ask.related_activities.map((related) => (
            <PromptForActivityRegistration
              key={related.id}
              title={related.title}
              id={related.id}
              dates={related.dates}
              baseActivityId={participationSummary.activity.id}
              forProgram={related.is_program_event}
              directlyConfirmable={related.is_directly_confirmable}
            />
          ))}
          {ask.upcoming_talks.length > 0 || ask.past_talks.length > 0 ? (
            <PromptForTalkAttendances
              activityId={participationSummary.activity.id}
              activityTitle={participationSummary.activity.title}
              upcomingTalks={ask.upcoming_talks}
              pastTalks={ask.past_talks}
              ongoingLabel={ask.ongoing_label}
            />
          ) : null}
        </Fragment>
      )}
    </Fragment>
  );
}

export function ResponsesRequested() {
  const { data } = useSelector(profileSlice.selector);
  const responsesNeededFor = data.participations
    .map((p) => {
      // Bring "activity" properties up to the top level for sorting
      const { start_date, end_date, title: activity_title } = p.activity;
      return { start_date, end_date, activity_title, ...p };
    })
    .filter((p) => p.items_needing_response)
    .sort(
      dualSort(
        "start_date",
        caseInsensitive,
        ascending,
        "activity_title",
        caseInsensitive,
        ascending
      )
    );

  const haveParticipationIn = Object.fromEntries(
    data.participations.map((p) => [p.activity.id, true])
  );
  const alsoAvailable = data.soliciting_registrations.filter(
    ({ related_to }) =>
      related_to.filter((id) => haveParticipationIn[id]).length === 0
  );

  if (responsesNeededFor.length === 0)
    return (
      <GeneralRegistrationSolicitation
        available={alsoAvailable}
        isAdditinonal={false}
      ></GeneralRegistrationSolicitation>
    );

  const onlyPastResponses =
    responsesNeededFor
      .map((p) => p.items_needing_response)
      .filter(
        (ask) =>
          ask.this_activity ||
          ask.related_activities.length > 0 ||
          ask.upcoming_talks.length > 0
      ).length === 0;

  const onlyPaymentIncomplete =
    responsesNeededFor
      .map((p) => p.items_needing_response)
      .filter((ask) => !ask.this_activity?.incomplete_payment).length === 0;

  return (
    <Fragment>
      <Container className="spaced">
        <Message color="info">
          <Message.Header>
            {onlyPastResponses
              ? "Did you Attend? Please let us Know"
              : onlyPaymentIncomplete
              ? "Not Yet Fully Paid"
              : "Are you Attending? Please Let Us Know"}
          </Message.Header>
          <Message.Body className="scrollable">
            {responsesNeededFor.map((p) => (
              <ResponsesRelatedToParticipation
                key={p.id}
                participationSummary={p}
              />
            ))}
          </Message.Body>
        </Message>
      </Container>
      <GeneralRegistrationSolicitation
        available={alsoAvailable}
        isAdditional={true}
      ></GeneralRegistrationSolicitation>
    </Fragment>
  );
}

function GeneralRegistrationSolicitation({ available, isAdditional }) {
  const navigate = useNavigate();
  if (available.length === 0) return null;
  return (
    <Container className="spaced">
      <Message>
        <Message.Header>
          {isAdditional
            ? "Other Upcoming Programs/Events Open for Registration"
            : "Upcoming Programs/Events Open for Registration"}
        </Message.Header>
        <Message.Body>
          <div className="block">
            The following programs and events are{" "}
            {isAdditional ? "also" : "now"} available for registration through
            this portal:
            <Select
              value=""
              onChange={(e) => {
                navigate("participation/" + e.target.value);
              }}
            >
              <option value="">Select a program or event ...</option>
              {available.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </Select>{" "}
          </div>

          <div className="block">
            Other programs and events (such as ongoing seminars) may also be
            available for registration; for the registration link, please refer
            to the program/event's web page on the{" "}
            <a href="https://www.fields.utoronto.ca">
              Fields Institute web site.
            </a>
          </div>
        </Message.Body>
      </Message>
    </Container>
  );
}
